"use client"
import { ComponentConfig } from "@measured/puck"
import { Modal } from "antd"
import Image from "next/image"
import { useState } from "react"
import { twMerge } from "tailwind-merge"
import { ADMIN_API_CONFIG } from "../../../../../../constants/api-configs"
import { DefaultImage } from "../../../../../../lib/external-components"
import { CommonStylesProps } from "../../lib/commonCSSProps"
import { generateResponsiveCSS } from "../../lib/helper"

export interface TeamMember {
  name: string
  position: string
  image: string
  description: string
}

export interface TeamMemberProps extends CommonStylesProps {
  title: string
  subtitle: string
  teamMembers: TeamMember[]
}

const RenderConfig: ComponentConfig<TeamMemberProps> = {
  render: ({ title, subtitle, teamMembers, responsiveType, className }) => {
    const id = `Team-${Math.random().toString(36).substr(2, 9)}`
    const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null)

    return (
      <>
        {generateResponsiveCSS(id, {}, responsiveType)}

        <div className={`${id} ${twMerge(className)} flex flex-col lg:gap-8 sm:gap-6 gap-4`}>
          {/* Header Section */}
          <div className="flex flex-col gap-3">
            <div className="flex items-center">
              <ul className="list-disc">
                <li className="lg:text-base lg:leading-[26px] sm:text-[15px] sm:leading-[24px] text-sm leading-[22px]">
                  {subtitle}
                </li>
              </ul>
            </div>
            <h2 className="lg:text-3.5xl lg:leading-[44px] sm:text-[26px] sm:leading-[36px] text-[22px] leading-[32px] text-[#07121F] font-semibold">
              {title}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 lg:gap-[43.5px] sm:gap-[30px] gap-[20px]">
            {teamMembers.map((member, index) => (
              <div
                key={index}
                className="overflow-hidden duration-300 cursor-pointer flex flex-col gap-5"
                onClick={() => setSelectedMember(member)}
              >
                <div className="relative h-[454px] rounded-3xl overflow-hidden">
                  <Image
                    src={member.image ? `${ADMIN_API_CONFIG.API_URL}${member.image}` : DefaultImage}
                    alt={member.name}
                    fill
                    className="object-cover transition-transform duration-300 hover:scale-105 h-auto"
                    loading="lazy"
                  />
                </div>

                <div className="">
                  <h3 className="lg:text-xl lg:leading-[30px] sm:text-lg text-base font-semibold">{member.name}</h3>
                  <p className="lg:text-base lg:leading-[26px] sm:text-[15px] sm:leading-[24px] text-sm leading-[22px]">
                    {member.position}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <Modal
            open={!!selectedMember}
            onCancel={() => setSelectedMember(null)}
            footer={null}
            centered
            className="team-modal rounded-3xl"
          >
            {selectedMember && (
              <div className="flex flex-col lg:flex-row lg:pt-[92px] lg:pb-10 lg:px-10 sm:p-6 p-4 lg:gap-6 gap-4">
                <div className="lg:w-[46.6%]">
                  <div className="relative h-[322px]">
                    <Image
                      src={selectedMember.image ? `${ADMIN_API_CONFIG.API_URL}${selectedMember.image}` : DefaultImage}
                      alt={selectedMember.name}
                      fill
                      className="object-cover rounded-xl h-auto"
                      sizes="(max-width: 1024px) 100vw, 50vw"
                    />
                  </div>
                </div>

                <div className="lg:w-[53.4%] flex flex-col gap-3">
                  <div>
                    <h3 className="lg:text-xl lg:leading-[30px] sm:text-lg text-base font-semibold text-[#121212]">
                      {selectedMember.name}
                    </h3>
                    <p className="lg:text-base lg:leading-[26px]">{selectedMember.position}</p>
                  </div>
                  <p className="lg:text-sm lg:leading-[24px] text-xs leading-[20px] text-[#3A455A]">
                    {selectedMember.description}
                  </p>
                </div>
              </div>
            )}
          </Modal>
        </div>
      </>
    )
  },
}

export { RenderConfig }
